import { MultiplayerGameType } from '../../constants/EntertainmentGames/multiplayerGame';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { AttachmentImage, I18n, UserId, Uuid } from '../Base';
import { RepositoryElement } from '../Repository';
import { MultiplayerGameJson, MultiplayerGameMode } from './MultiplayerGame';

export type I18nGameInfo = I18n<{
  title: string;
}>;

type TeamPlayer = {
  buddyId: string;
  appearance: number;
  hasViewedRoundOrder?: boolean;
  userId: UserId;
  isReady: boolean;
  isTimeout?: boolean;
  botDifficulty?: number;
};

type Team = {
  color: string;
  score: number;
  players: TeamPlayer[];
};

type Move = {
  movedAt: number;
  piece: string;
  position: string;
};

type Round = {
  startedAt: number;
  team: number;
  notifiedAt: number;
  moves: Move[];
  player: number;
  reward?: {
    type: string;
    move?: number;
    collectedAt?: number;
  };
};

export type MultiplayerGameSessionJson<T = number> = {
  sessionId: Uuid;
  classroomId: Uuid;
  multiplayerGameId: Uuid;
  createdBy: UserId;
  createdAt: T;
  startedAt: T;
  endedAt: T;
  gameType: MultiplayerGameType;
  i18nGameInfo: I18nGameInfo | undefined;
  mode: MultiplayerGameMode;
  thinkingTimeLimit: number; // in sec
  responseTimeLimit: number; // in sec
  background: AttachmentImage | null;
  gameData: MultiplayerGameJson['gameData'];
  moveFee: {
    token: number;
  };
  teams: Team[];
  roundOrder: number[];
  roundOrderWillConfirmAt: T;
  rounds: Round[][];
  rewardPool: {
    exp: number;
    joyPoint: number;
    classroomCredit: number;
  };
  winner: number;
};

export class MultiplayerGameSession
  extends RepositoryElement
  implements MultiplayerGameSessionJson<Date>
{
  readonly sessionId!: Uuid;
  classroomId!: Uuid;
  multiplayerGameId!: Uuid;
  createdBy!: UserId;
  createdAt!: Date;
  startedAt!: Date;
  endedAt!: Date;
  gameType!: MultiplayerGameType;
  i18nGameInfo!: I18nGameInfo | undefined;
  mode!: MultiplayerGameMode;
  thinkingTimeLimit!: number;
  responseTimeLimit!: number;
  background!: AttachmentImage | null;
  gameData!: MultiplayerGameJson['gameData'];
  moveFee!: {
    token: number;
  };
  teams!: Team[];
  roundOrder!: number[];
  roundOrderWillConfirmAt!: Date;
  rounds!: Round[][];
  rewardPool!: {
    exp: number;
    joyPoint: number;
    classroomCredit: number;
  };
  winner!: number;

  constructor(sessionData: MultiplayerGameSessionJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(sessionData));
  }

  id() {
    return this.sessionId;
  }

  toJSON(): MultiplayerGameSessionJson<number> {
    return deepTransformDate(
      this,
    ) as unknown as MultiplayerGameSessionJson<number>;
  }
}
